import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax

export const clusterSourceId = 'poi-clusters';
export const clusterLayerId = 'clusters';
export const clusterCountLayerId = 'cluster-count';
export const unclusteredLayerId = 'unclustered-point';

export const deactivateAllPoisAndCategories = (settings) => {
  for (const id in settings.poiCategories) {
    settings.poiCategories[id].active = false;
  }
  for (const id in settings.pois) {
    settings.pois[id].active = false;
    if (Array.isArray(settings.pois[id].pois)) {
      settings.pois[id].pois.forEach(p => { p.active = false; });
    }
  }
};

export const addPoi = (poi, parent = null, poiId, globalMapSettings, poiMarkers, generateMarker, map, pinnedPoiIds = null) => {
  const isPinned = pinnedPoiIds?.has(poi.id);
  if ((poi.type === 'track' || poi.type === 'route') && !poi.active && !isPinned) return;
  if (!isPinned && !poi.active && !globalMapSettings.poiCategories?.[parent ? parent.iconId : poi.iconId]?.active) return;
  if (poiMarkers.current[poi.id]) return;

  poi.pois?.forEach(x => addPoi(x, poi, poiId, globalMapSettings, poiMarkers, generateMarker, map, pinnedPoiIds));

  let marker = !poiId
    ? generateMarker('poi', poi)
    : (poi.id === poiId) ? generateMarker('poi', poi) : null

  if (marker === null) return
  marker.addTo(map);

  poiMarkers.current[poi.id] = marker;
}

export const focusOnPoiType = (poi, map, isShowAllPressed) => {
  if (isShowAllPressed) return;
  let trackBounds = {
    west: null,
    south: null,
    east: null,
    north: null
  };

  if (trackBounds.west === null) {
    trackBounds = {
      west: poi.waypoints[0].longitude,
      south: poi.waypoints[0].latitude,
      east: poi.waypoints[0].longitude,
      north: poi.waypoints[0].latitude
    };
  }

  //find edge boundaries of poiType
  for (const { latitude, longitude } of poi.waypoints) {
    if (trackBounds.north < latitude) trackBounds.north = latitude;
    if (trackBounds.south > latitude) trackBounds.south = latitude;
    if (trackBounds.west > longitude) trackBounds.west = longitude;
    if (trackBounds.east < longitude) trackBounds.east = longitude;
  }

  const bounds = new mapboxgl.LngLatBounds(
    [trackBounds.west, trackBounds.south],
    [trackBounds.east, trackBounds.north],
  );

  const { west, south, east, north } = trackBounds;
  if (
    [west, south, east, north].some(v => typeof v !== 'number' || isNaN(v))
  ) {
    console.warn('Invalid bounds', trackBounds);
    return;
  }

  map.fitBounds(bounds, {
    padding: 150
  });
};

export const addPoiType = (poi, map, generateMarker, poiTypeMarkers, globalMapSettings, poiMarkers, poiId = null, pinnedPoiIds = null) => {
  if(poiTypeMarkers.current[poi.id]) return;
  const key = poi.id.toString();

  poi.pois?.forEach(x => addPoi(x, poi, poiId, globalMapSettings, poiMarkers, generateMarker, map, pinnedPoiIds));
  let marker = !poiId
    ? generateMarker('poi', poi)
    : (key === poiId) ? generateMarker('poi', poi) : null

  if (marker === null) return
  marker.addTo(map);
  poiTypeMarkers.current[key] = marker;

  const isPinned = pinnedPoiIds?.has(poi.id);
  if ((!poi.active && !isPinned) || (poi.type !== 'route' && poi.type !== 'track')) return;
  const sourceId = `poi-type-${key}`;

  if (!map.getSource(sourceId)) {
    try {
      map.addSource('poi-type-' + key, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: poi.waypoints.map(wp => [wp.longitude, wp.latitude]),
          }
        },
      });
    } catch (e) {
      console.warn('Source add failed', e);
    }
  }
  const beforeLayerId = map.getLayer(clusterLayerId) ? clusterLayerId : undefined;
  if (!map.getLayer('poi-outline-' + key)) {
    try {
      map.addLayer({
        id: 'poi-outline-' + key,
        type: 'line',
        source: 'poi-type-' + key,
        paint: {
          'line-color': '#000000',
          'line-width': 10,
        },
      }, beforeLayerId);
    } catch (e) {
      console.warn('Layer add failed', e);
    }
  }

  if (!map.getLayer('poi' + key)) {
    try {
      map.addLayer({
        id: 'poi' + key,
        type: 'line',
        source: 'poi-type-' + key,
        paint: {
          'line-color': poi.color ?? '#ff0000',
          'line-width': 6,
        },
      }, beforeLayerId);
    } catch (e) {
      console.warn('Layer add failed', e);
    }
  }
};

export const safelyRemoveAllMarkers = (markerMap) => {
  Object.values(markerMap.current).forEach(marker => {
    try {
      marker.remove();
    } catch {}
  });
  markerMap.current = {};
};

export const toggleAllPoiTypeLayersExceptActive = (map, displayedPoiTypesValues, visible, exceptId) => {
  displayedPoiTypesValues?.forEach(poi => {
    const key = poi.id.toString();
    const isTarget = key === exceptId;

    const layers = [`poi-outline-${key}`, `poi${key}`];

    layers.forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', isTarget ? 'visible' : (visible ? 'visible' : 'none'));
      }
    });
  });
};

export const toggleAllPoiTypeMarkersExceptActive = (map, poiTypeMarkers, visible, exceptId) => {
  Object.entries(poiTypeMarkers.current)?.forEach(([key, marker]) => {
    const el = marker.getElement?.();
    if (el) {
      const isTarget = key === exceptId;
      el.style.display = isTarget ? '' : (visible ? '' : 'none');

      // toggle popup
      const popup = marker.getPopup?.();
      if (popup && map) {
        if (isTarget) {
          if (!popup.isOpen()) {
           popup.addTo(map);
          }
        } else {
          popup.remove();
        }
      }
    }
  });
};

export const debounce = (func, delay) => {
  let timer;
  return function (...args) {
    const context = this;
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(context, args), delay);
  };
};

// Grouping by arrays of poi with the same coordinates
export const groupingPoisWithSameCoordinates = (pois) => {
  const coordinateGroups = {};

  Object.values(pois).forEach((poi) => {
    const key = `${poi.latitude},${poi.longitude}`;
    if (!coordinateGroups[key]) {
      coordinateGroups[key] = [];
    }
    coordinateGroups[key].push({ ...poi });
  });

  const groupedPois = Object.values(coordinateGroups).filter(group => group.length > 1);

  groupedPois.forEach(group => {
    group.forEach((poi, i) => {
      poi.latitude += i * 0.00005;
      poi.longitude += i * 0.00005;
    });
  });

  return groupedPois;
};

export const createLocationMarkerElement = (markerLabel, markerColor) => {
  const el = document.createElement('DIV');
  el.style.width = '40px';
  el.style.height = '40px';

  const fillImg = document.createElement('img');
  fillImg.src = '/icons/map_location_pin_fill.svg';
  fillImg.style.position = 'absolute';
  fillImg.style.top = '0';
  fillImg.style.left = '0';
  fillImg.style.width = '100%';
  fillImg.style.height = '100%';
  fillImg.style.objectFit = 'contain';

  const frameImg = document.createElement('img');
  frameImg.src = '/icons/map_location_pin_frame.svg';
  frameImg.style.position = 'absolute';
  frameImg.style.top = '0';
  frameImg.style.left = '0';
  frameImg.style.width = '100%';
  frameImg.style.height = '100%';
  frameImg.style.objectFit = 'contain';

  if (markerLabel) {
    const label = document.createElement('DIV');
    label.textContent = markerLabel;
    label.style.position = 'relative';
    label.style.zIndex = '2';
    label.style.top = '50%';
    label.style.left = '50%';
    label.style.transform = 'translate(-50%, -50%)';
    label.style.fontSize = '16px';
    label.style.color = `#${markerColor}`;
    label.style.pointerEvents = 'none';
    el.appendChild(label);
  }

  el.appendChild(fillImg);
  el.appendChild(frameImg);

  return el;
}